import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './PublicLayout.css';
import { useTemporada } from '../context/TemporadaContext';
import { useAuth } from '../context/AuthContext';
import { usePantallas } from '../context/PantallasContext';
import GuardaPantalla from './GuardaPantalla';
import { getTemporadasReal } from '../services/temporadasService';
import { logout } from '../services/authService';

const NAV_ITEMS = [
  { to: '/clasificacion', label: 'Clasificación' },
  { to: '/jugadores',   label: 'Jugadores' },
  { to: '/jornadas',    label: 'Jornadas' },
  { to: '/calendario',  label: 'Calendario' },
  { to: '/competicion', label: 'Pistas' },
];

function PublicLayout() {
  const { temporada, setTemporada } = useTemporada();
  const [temporadas, setTemporadas] = useState([]);
  const [temporadasCargadas, setTemporadasCargadas] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { usuario, recargar } = useAuth();
  const { esVisible } = usePantallas();
  const esGestor = usuario?.rol === 'ADMIN' || usuario?.rol === 'COMITE';
  const navItems = [
    ...(usuario?.jugadorId ? [{ to: '/mi-area-personal', label: 'Mi área personal' }] : []),
    ...NAV_ITEMS,
    ...(usuario?.jugadorId || esGestor ? [{ to: '/aplazamiento', label: 'Aplazamiento' }] : []),
  ]
    .filter(({ to }) => esVisible(to));

  // Sin ninguna temporada visible, público y jugador no ven nada (gestión sí).
  const sinTemporadas = temporadasCargadas && temporadas.length === 0 && !esGestor;

  const cerrarSesion = async () => {
    try { await logout(); } catch { /* sin sesión o sin red: se sigue igual */ }
    await recargar();
    navigate('/jornadas');
  };

  // En móvil el menú es un panel desplegable: se cierra al cambiar de pantalla.
  useEffect(() => { setMenuAbierto(false); }, [location.pathname]);

  // Escape o un clic fuera del panel lo cierran.
  useEffect(() => {
    if (!menuAbierto) return undefined;
    const alTecla = (e) => { if (e.key === 'Escape') setMenuAbierto(false); };
    const alClic = (e) => { if (!e.target.closest('.public-header-inner')) setMenuAbierto(false); };
    document.addEventListener('keydown', alTecla);
    document.addEventListener('mousedown', alClic);
    return () => {
      document.removeEventListener('keydown', alTecla);
      document.removeEventListener('mousedown', alClic);
    };
  }, [menuAbierto]);

  const seccionActual = navItems.find(({ to }) => to === location.pathname)?.label;

  useEffect(() => {
    setTemporadasCargadas(false);
    getTemporadasReal().then(ts => {
      setTemporadas(ts);
      setTemporadasCargadas(true);
      if (!temporada || !ts.find(t => t.id === temporada)) {
        const activa = ts.find(t => t.estado === 'INICIADA') ?? ts[ts.length - 1];
        if (activa) setTemporada(activa.id);
      }
    }).catch(() => {});
  }, [usuario?.rol]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="public-layout">
      <header className="public-header">
        <div className="public-header-inner">
          <Link to="/jornadas" className="public-brand">
            <span
              className="public-brand-icon"
              aria-hidden="true"
              style={{ '--volante': `url(${process.env.PUBLIC_URL}/volante.svg)` }}
            />
            <span className="public-brand-text">Liga Bádminton</span>
          </Link>

          {seccionActual && <span className="public-seccion-actual">{seccionActual}</span>}

          <button
            type="button"
            className="public-menu-toggle"
            onClick={() => setMenuAbierto(a => !a)}
            aria-label={menuAbierto ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuAbierto}
            aria-controls="menu-publico"
          >
            <span className="public-menu-toggle-barras" aria-hidden="true" />
          </button>

          {/* En escritorio `display: contents` lo deja transparente; en móvil es el panel desplegable. */}
          <div id="menu-publico" className={`public-menu${menuAbierto ? ' abierto' : ''}`}>
          <nav className="public-nav">
            {navItems.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`public-nav-link${location.pathname === to ? ' active' : ''}`}
              >
                {label}
              </Link>
            ))}
          </nav>

          <div className="public-header-actions">
            {temporadas.length === 1 && (
              <span className="public-temporada-texto">{temporadas[0].nombre}</span>
            )}
            {temporadas.length > 1 && (
              <select
                className="public-temporada-select"
                value={temporada ?? ''}
                onChange={e => setTemporada(e.target.value)}
              >
                {temporadas.map(t => (
                  <option key={t.id} value={t.id}>{t.nombre}</option>
                ))}
              </select>
            )}
            {usuario ? (
              <>
                {esGestor && <Link to="/dashboard" className="public-login-btn">Gestión</Link>}
                <button type="button" className="public-login-btn public-logout-btn" onClick={cerrarSesion}>Salir</button>
              </>
            ) : null /* El botón «Entrar» no se ofrece nunca; el login se alcanza por URL */}
          </div>
          </div>
        </div>
      </header>

      <main className="public-main">
        {sinTemporadas
          ? (
            <div className="pantalla-oculta" role="status">
              <h2>No hay temporadas activas</h2>
              <p>Ahora mismo no hay ninguna temporada disponible. Vuelve a intentarlo más tarde.</p>
            </div>
          )
          : <GuardaPantalla inicio="/jornadas" />}
      </main>
    </div>
  );
}

export default PublicLayout;
